import { colors } from '@salonomy/design-tokens';
import type { ChairAccent } from '../diary/DiaryGrid.js';
import { CHAIR_ACCENTS } from '../diary/accents.js';

/**
 * WS11 — "My week" location colour-coding. The unified diary spans several tenants, so unlike the
 * per-chair diary (which colours COLUMNS within ONE salon) we colour by PLACE: one stable accent per
 * salonId, plus a reserved cobalt accent for the practitioner's own studio. Pure + side-effect-free so a
 * vitest unit can cover stability exactly like features/residency/schedule.ts (the day-grouping precedent).
 *
 * VISUAL LANGUAGE (no new palette): every accent is a member of the diary's CHAIR_ACCENTS — the explicit
 * soft tints / rgba shadows (no `color-mix`) chosen for the 5-year-old-iPad Safari target (accents.ts).
 * We never invent a colour; we only ASSIGN existing accents to places.
 *
 * STABILITY CONTRACT (what the test pins): for a given set of legs the colour of each place is
 * deterministic and order-independent — salonIds are sorted, then accents are dealt round-robin from a
 * fixed offset. The private studio is ALWAYS the cobalt accent (CHAIR_ACCENTS[0], the brand token), so a
 * practitioner's own studio reads the same wherever it appears (brief 11: private practice = cobalt).
 */

/** The private studio's accent — the brand cobalt treatment (CHAIR_ACCENTS[0]); reserved, never reused. */
export const STUDIO_ACCENT: ChairAccent = CHAIR_ACCENTS[0]!;

/** The salon accents — every CHAIR_ACCENT except the cobalt one reserved for the studio. */
const SALON_ACCENTS: ChairAccent[] = CHAIR_ACCENTS.filter((a) => a.accent !== colors.cobalt);

/** A leg of the week the colour map is built over (the salonId tag + whether it is the own studio). */
export interface PlaceLeg {
  salonId: string;
  isPrivatePractice: boolean;
}

/** A resolved place colour for the legend + the per-item pills. */
export interface PlaceColor {
  salonId: string;
  accent: ChairAccent;
  isPrivatePractice: boolean;
}

/**
 * Build the stable salonId → accent map. The private-practice salonId (if any) takes the reserved studio
 * accent; the remaining distinct salonIds are sorted (so the map is order-independent) and dealt the
 * SALON_ACCENTS round-robin. With more salons than salon-accents the palette repeats — acceptable for the
 * at-a-glance legend (best-effort this slice; a richer palette is a later design-round concern).
 */
export function buildLocationColors(legs: PlaceLeg[]): Map<string, PlaceColor> {
  const map = new Map<string, PlaceColor>();

  // The own studio always reads cobalt — capture its salonId first so it is never dealt a salon accent.
  const studioIds = new Set(legs.filter((l) => l.isPrivatePractice).map((l) => l.salonId));
  for (const salonId of studioIds) {
    map.set(salonId, { salonId, accent: STUDIO_ACCENT, isPrivatePractice: true });
  }

  // Distinct, sorted salonIds (excluding the studio) get a stable round-robin salon accent.
  const salonIds = [
    ...new Set(legs.filter((l) => !studioIds.has(l.salonId)).map((l) => l.salonId)),
  ].sort((a, b) => a.localeCompare(b));
  salonIds.forEach((salonId, i) => {
    map.set(salonId, {
      salonId,
      accent: SALON_ACCENTS[i % SALON_ACCENTS.length]!,
      isPrivatePractice: false,
    });
  });

  return map;
}

/** The accent for one place — falls back to the studio/cobalt accent if a salonId is somehow unmapped. */
export function colorFor(map: Map<string, PlaceColor>, salonId: string): ChairAccent {
  return map.get(salonId)?.accent ?? STUDIO_ACCENT;
}

/** The label for a place — the studio is suffixed "(your studio)" per the brief; salons show their name. */
export function placeLabel(salonName: string, isPrivatePractice: boolean): string {
  return isPrivatePractice ? `${salonName} (your studio)` : salonName;
}

interface DateGroupable {
  startAt: string;
}

/** One day's worth of week items, keyed by the salon-zone date, with items sorted by start time. */
export interface DayGroup<T extends DateGroupable> {
  date: string;
  items: T[];
}

/**
 * Group week items by their salon-zone date, ascending by date then by start time. The cross-location
 * flagship shape is a day-grouped agenda (NOT the per-chair 2D grid — columns are per-chair-in-one-tenant),
 * matching the mobile owner "This week" screen. `dateOf` projects an item to its "YYYY-MM-DD" salon-zone
 * day (the caller supplies isoDateInZone bound to the active tz) — kept injectable so this stays pure.
 */
export function groupByDay<T extends DateGroupable>(
  items: T[],
  dateOf: (iso: string) => string,
): DayGroup<T>[] {
  const byDate = new Map<string, T[]>();
  for (const it of items) {
    const d = dateOf(it.startAt);
    const bucket = byDate.get(d);
    if (bucket) bucket.push(it);
    else byDate.set(d, [it]);
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, dayItems]) => ({
      date,
      items: [...dayItems].sort((a, b) => a.startAt.localeCompare(b.startAt)),
    }));
}
